import { useEffect, useCallback } from 'react';
import { addGlobalError } from './ErrorNotificationStatus';
import { getAllData } from '../../utils/helpers/fetching';
import { API } from 'aws-amplify';

/**
 * Custom hook to handle GraphQL errors and display them via the error tooltip
 * @param {Object} error - The error object from GraphQL query/mutation
 * @param {string} operationName - Optional name of the GraphQL operation for debugging
 * @param {number} autoDismissSeconds - Seconds before error auto-dismisses (default: 7)
 */
export const useGraphQLErrorHandler = (error, operationName = '', autoDismissSeconds = 7) => {
  useEffect(() => {
    if (error) {
      // Add a unique ID based on operation name and timestamp for better tracking
      const errorId = `${operationName}-${Date.now()}`;
      addGlobalError(error, errorId, autoDismissSeconds);
    }
  }, [error, operationName, autoDismissSeconds]);
};

/**
 * Hook to wrap getAllData calls and automatically handle errors
 * @param {string} operationName - Name of the operation for error tracking
 * @param {number} autoDismissSeconds - Seconds before error auto-dismisses (default: 7)
 */
export const useGraphQLErrorHandling = (operationName = '', autoDismissSeconds = 7) => {
  const handleGetAllData = useCallback(async (getAllDataParams) => {
    try {
      const result = await getAllData(getAllDataParams);
      // Check if the result contains an error
      if (result?.error) {
        const errorId = `${operationName}-${Date.now()}`;
        addGlobalError(result.error, errorId, autoDismissSeconds);
      }

      return result;
    } catch (error) {
      const errorId = `${operationName}-${Date.now()}`;
      addGlobalError(error, errorId, autoDismissSeconds);
      throw error; // Re-throw to maintain original behavior
    }
  }, [operationName, autoDismissSeconds]);

  const handleApiGraphql = useCallback(async (apiGraphqlParams) => {
    try {
      const result = await API.graphql(apiGraphqlParams);

      // Check if the result contains GraphQL errors
      if (result?.errors && Array.isArray(result.errors) && result.errors.length > 0) {
        const errorId = `${operationName}-${Date.now()}`;
        addGlobalError(result, errorId, autoDismissSeconds);
      }

      return result;
    } catch (error) {
      const errorId = `${operationName}-${Date.now()}`;
      addGlobalError(error, errorId, autoDismissSeconds);
      throw error; // Re-throw to maintain original behavior
    }
  }, [operationName, autoDismissSeconds]);

  return {
    handleGetAllData,
    handleApiGraphql
  };
};

/**
 * Higher-order function to wrap GraphQL operations and automatically handle errors
 * @param {Function} operation - The GraphQL operation function
 * @param {string} operationName - Name of the operation for error tracking
 * @param {number} autoDismissSeconds - Seconds before error auto-dismisses (default: 7)
 */
export const withErrorHandling = (operation, operationName = '', autoDismissSeconds = 7) => {
  return async (...args) => {
    try {
      const result = await operation(...args);

      // Check if the result contains GraphQL errors
      if (result?.errors && Array.isArray(result.errors) && result.errors.length > 0) {
        const errorId = `${operationName}-${Date.now()}`;
        addGlobalError(result, errorId, autoDismissSeconds);
      }

      return result;
    } catch (error) {
      const errorId = `${operationName}-${Date.now()}`;
      addGlobalError(error, errorId, autoDismissSeconds);
      throw error; // Re-throw to maintain original behavior
    }
  };
};
